import * as React from 'react'

import { Button } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import Text from 'modules/core/components/Inputs/Text'
import Modal from 'modules/core/components/Modal'
import { trackingEvents } from 'modules/core/lib/Tracking'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import { useReportTracking } from 'modules/reports/hooks/useReportTracking'
import useSaveReport from 'modules/reports/hooks/useSaveReport'
import { type ReportEditorStore } from 'modules/reports/lib/Store'
import type Report from 'modules/reports/store/models/Report'

import ErrorModal from './errorModal'
import ExecuteQueryModal from './executeQueryModal'

const ReportNameModal = ({
  report,
  store,
  onSuccess,
  onClose,
  clone = false,
}: {
  report: Report
  store: ReportEditorStore
  onSuccess?: () => void
  onClose: () => void
  clone?: boolean
}) => {
  const { track } = useReportTracking(store)
  const { onSaveReport, saving } = useSaveReport({
    report,
    store,
    onSuccess,
    clone,
  })

  const onSubmit = () => {
    track(trackingEvents.reports.clickSaveWidgetFromBuilder)

    if (report.isNew) {
      track(trackingEvents.reports.newReportSavedButtonClicked)
    }

    onSaveReport()
  }

  if (!hasFeature('dev_disable_reports_on_save_frontend_check')) {
    if (store.sqlQuery) {
      if (!store.sqlQueryExecuted) {
        return <ExecuteQueryModal store={store} onClose={onClose} />
      }
      if (store.sqlQueryError) {
        return <ErrorModal onClose={onClose} />
      }
    } else if (store.builderReportHadErrors) {
      // The builder auto-fetches content when something changes
      return <ErrorModal onClose={onClose} />
    }
  }

  const disabled = saving || !store.reportForm.get('title').value

  return (
    <Modal
      title={
        report.isNew
          ? i18n.t('custom_reports.new_report.title')
          : i18n.t('custom_reports.edit_report')
      }
      description={i18n.t('custom_reports.new_report.description')}
      illustration="report-library"
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button type="negative" disabled={disabled} submit>
            {i18n.t('custom_reports.new_report.save')}
          </Button>
        </>
      }
    >
      <FormRow>
        <Text
          label={i18n.t('custom_reports.new_report.report_name')}
          field={store.reportForm.get('title')}
          readonly={saving}
        />
      </FormRow>
    </Modal>
  )
}

export default observer(ReportNameModal)
